import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import * as FileSaver from 'file-saver';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import { Contact, User, UserTab } from '../../../auth/models';
import { ToastService } from '../../../main/services';
import { Supplier } from '../../models/supplier';
import { Tender } from '../../models/tender';
import {
    ProposalService,
    SealedTenderService,
    SuppliersService,
    TenderSupplierInvitationInfoService,
    TenderSuppliersService
} from '../../services';
import { TenderLaunchingXlsExportService } from '../../services/tender-launching-xls-export/tender-launching-xls-export.service';
import { ConfirmModalComponent } from '../../../common/components';
import {
    AddInvitedSuppliersFormModalComponent,
    CreateSupplierFormModalComponent
} from './../create-tender-suppliers';
import { InvitationInfoModalComponent } from './components/invitation-info-modal/invitation-info-modal.component';
import { chipLabel, chipClass } from './bid-progress-chips';

@Component({
    selector: 'jft-tender-suppliers',
    templateUrl: './tender-suppliers.component.html',
    styleUrls: ['./tender-suppliers.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TenderSuppliersComponent implements OnInit {
    public supplier: Supplier;
    public suppliers: Supplier[] = [];
    public isAllSuppliersSubmitted = false;
    public notInvitedSuppliers: User[] = [];
    public invitedSuppliers: User[] = [];
    public tender: Tender;
    private termObservable = new Subject();
    private termSubscription: Subscription;
    public availableTabs: UserTab[] = [];
    public suppliersArchived: User[] = [];
    public selectedTags: any[] = [];
    public tags: any[] = [];
    public invitationStatus: string = 'all';
    public invitationStatusOptions = [
        { value: 'all', label: 'All Suppliers' },
        { value: 'accepted', label: 'Accepted Invitations' },
        { value: 'pending', label: 'Pending Invitations' },
        { value: 'declined', label: 'Declined Invitations' }
    ];
    public bidProgressFilter: string = 'all';
    public bidProgressOptions: { value: string, label: string }[] = [
        { value: 'all', label: 'All Progress' },
    ];
    public engagementFilter: string = 'all';
    public engagementOptions = [
        { value: 'all', label: 'All Suppliers' },
        { value: 'not_opened', label: 'Not Opened' },
        { value: 'opened', label: 'Opened' },
    ];
    public chipLabel = chipLabel;
    public chipClass = chipClass;

    constructor(
        private route: ActivatedRoute,
        private toast: ToastService,
        private service: TenderSuppliersService,
        private proposalService: ProposalService,
        private suppliersService: SuppliersService,
        private invitationService: TenderSupplierInvitationInfoService,
        private injector: Injector,
        private ngbModal: NgbModal,
        private sealedTenderService: SealedTenderService,
        private tenderExportXlsService: TenderLaunchingXlsExportService,
        public user: User
    ) {
    }

    public totalItems: number = 0;
    public totalUsers: number = 0;
    public page: number = 1;
    public loadingMore: boolean = false;

    public ngOnInit() {
        let data = this.route.snapshot.data;
        let parentData = this.route.parent.snapshot.data;
        this.suppliers = data['suppliers'];
        // Note: Resolver only fetches page 1. Since we don't return count in resolver (it returns Supplier[]),
        // we might not know total count initially unless we fetch again or adjust resolver.
        // For simplicity, we can fetch count in ngOnInit or just let refresh reset it.
        // Or assume if suppliers.length < 20, that's it.
        // Better approach: Let's trigger a refresh to align state or update resolver to return {results, count}.
        // However, updating resolver return type might break other things.
        // Let's rely on refreshSuppliers to set totalItems correctly when filters change.
        // For initial load, we might miss totalItems if > 20.
        // We will call refreshSuppliers(tags, 1) to sync initial state properly if needed, but that doubles the call.
        // Alternatively, we leave totalItems 0 until first interact or load more check.
        // Actually, if we want Load More to appear, we need totalItems.
        // Let's do a quick fetch on init to get metadata if needed, OR just assume if equal to page size (20) there might be more.

        // Optimizing: Just call refreshSuppliers to ensure we have count and sorted backend state
        this.tender = parentData['tender'];
        this.page = 1;
        this.refreshSuppliers([], 1);

        this.availableTabs = data['availableTabs'];
        this.searchInit();
        this.getNotInvitedSuppliers();
        this.initAvailableTabs();
        this.isAllSuppliersSubmitted = this.suppliers.filter(supplier => supplier.statusView === 'Submitted').length === this.suppliers.length
    }

    private initAvailableTabs() {
        this.availableTabs.forEach((c, i) => {
            this.tags.push({ id: c.id, name: c.name });
        });
    }

    public tagChange(tags: any[]) {
        this.refreshSuppliers(tags.map((item: any) => item.id), 1);
    }

    private getNotInvitedSuppliers() {
        let suppliersIds = [];
        this.suppliers.forEach((company: Supplier) => {
            company.users.forEach(user => {
                suppliersIds.push(user.id);
            })
        })
        this.suppliersService.fetchUsers().subscribe(suppliers => {
            this.notInvitedSuppliers = suppliers.filter((user: User) => {
                return !suppliersIds.includes(user.id);
            });
            this.invitedSuppliers = suppliers.filter((user: User) => {
                return suppliersIds.includes(user.id);
            });
        })
    }

    private searchInit() {
        this.termSubscription = this.termObservable
            .pipe(debounceTime(500))
            .pipe(distinctUntilChanged())
            .subscribe((term: string) => {
                this.service.setFilter(term);
                this.refreshSuppliers([], 1);
            });
    }

    private refreshSuppliers(tags = [], page = 1) {
        if (page === 1) {
            this.page = 1;
        }

        this.service.fetchLastRound(this.tender.id, tags, this.invitationStatus, page, null, 20, this.bidProgressFilter, this.engagementFilter).subscribe((data) => {
            let newSuppliers = data.results;
            this.totalItems = data.count;
            this.totalUsers = data.total_users;

            if (data.bid_progress_options.length) {
                this.bidProgressOptions = [
                    { value: 'all', label: 'All Progress' },
                    ...data.bid_progress_options.map((bucket: string) => ({
                        value: bucket,
                        label: bucket,
                    })),
                ];
            }

            if (page === 1) {
                this.suppliers = newSuppliers;
            } else {
                this.suppliers = [...this.suppliers, ...newSuppliers];
            }

            this.loadingMore = false;
            this.getNotInvitedSuppliers();
        });
    }

    public loadMore() {
        this.loadingMore = true;
        this.page++;
        // Use current tags
        const tags = this.selectedTags ? this.selectedTags.map(t => t.id) : [];
        this.refreshSuppliers(tags, this.page);
    }

    public search(term) {
        this.termObservable.next(term);
    }

    public onInvitationStatusChange() {
        this.refreshSuppliers(this.selectedTags);
    }

    public onBidProgressFilterChange() {
        this.refreshSuppliers(this.selectedTags);
    }

    public getInvitationSupplierInfo(supplier: Supplier, user: Contact) {
        this.openInvitationSupplierInfoModal(supplier, user);
    }

    public confirmSendInvitation(supplier: Supplier) {
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Re-send Invitation';
        modalRef.componentInstance.text = `Re-send invitation email to all listed users at ${supplier.name}?`;
        modalRef.componentInstance.confirmButtonText = 'Send';
        modalRef.componentInstance.confirmButtonClass = 'btn-primary';
        modalRef.componentInstance.confirmButtonIcon = 'fas fa-envelope';
        modalRef.result.then((confirmed) => {
            if (confirmed) {
                this.invitationService.send(this.tender.id, supplier.id).subscribe({
                    next: () => {
                        this.toast.success(`Invitation email to ${supplier.name} has been sent`);
                    },
                    error: (e) => {
                        this.toast.error(e?.error?.error || 'Failed to send invitation');
                    }
                });
            }
        }, () => {});
    }

    public confirmSendInvitationToUser(supplier: Supplier, user: Contact) {
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Re-send Invitation';
        modalRef.componentInstance.text = `Re-send invitation email to ${user.firstName} ${user.lastName}?`;
        modalRef.componentInstance.confirmButtonText = 'Send';
        modalRef.componentInstance.confirmButtonClass = 'btn-primary';
        modalRef.componentInstance.confirmButtonIcon = 'fas fa-envelope';
        modalRef.result.then((confirmed) => {
            if (confirmed) {
                this.invitationService.sendToUser(this.tender.id, supplier.id, user.id).subscribe({
                    next: () => {
                        this.toast.success(`Invitation email to ${user.firstName} ${user.lastName} has been sent`);
                    },
                    error: (e) => {
                        this.toast.error(e?.error?.error || 'Failed to send invitation');
                    }
                });
            }
        }, () => {});
    }

    public openInvitationSupplierInfoModal(supplier: Supplier, user: Contact) {
        this.ngbModal.open(InvitationInfoModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
                { provide: Supplier, useValue: supplier },
                { provide: Contact, useValue: user }
            ], this.injector),
            size: 'lg',
            backdrop: 'static',
            windowClass: 'invitation-info-modal'
        }).result.then((data) => {

        }, (error) => {
        });
    }

    public ngOnDestroy(): void {
        this.termSubscription.unsubscribe();
    }

    public createSupplierManually() {
        const modal: NgbModalRef = this.ngbModal.open(CreateSupplierFormModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
                { provide: Supplier, useValue: this.supplier },
            ], this.injector),
            size: 'lg',
            backdrop: 'static'
        })
        modal.result.then((data) => {
            this.refreshSuppliers();
        }, (error) => { });
        modal.componentInstance.availableTags = this.availableTabs;
    }

    public addSuppliersFromContacts() {
        const modal: NgbModalRef = this.ngbModal.open(AddInvitedSuppliersFormModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
            ], this.injector),
            fullscreen: true,
            backdrop: 'static'
        })
        modal.result.then((data) => {
            this.addSuppliersToLaunchedTender(data.users);
        }, (error) => { });
        modal.componentInstance.availableTags = this.availableTabs;
    }

    public reInviteSuppliersFromContacts() {
        const modal: NgbModalRef = this.ngbModal.open(AddInvitedSuppliersFormModalComponent, {
            injector: Injector.create([
                { provide: Tender, useValue: this.tender },
            ], this.injector),
            fullscreen: true,
            backdrop: 'static'
        })
        modal.result.then((data) => {
            this.addSuppliersToLaunchedTender(data.users);
        }, (error) => { });
        modal.componentInstance.availableTags = this.availableTabs;
        modal.componentInstance.showAllUsers = true;
        modal.componentInstance.allSuppliersButton = false;
    }

    private addSuppliersToLaunchedTender(suppliers: User[]) {
        suppliers.forEach(user => {
            this.addSupplierToLaunchedTender(user);
        })
    }

    private addSupplierToLaunchedTender(user: User) {
        this.service.addSupplierToLaunchedTender(this.tender, user).subscribe(data => {
            this.toast.success(`Suppliers have been added`);
            this.refreshSuppliers();
            this.proposalService.onBidSubmitted.emit();
        }, (e) => {
            this.toast.error(e);
        });
    }

    public downloadSuppliersTemplate($event: Event) {
        $event.preventDefault();
        $event.stopPropagation();

        this.service.downloadSuppliersTemplate(this.tender)
            .pipe(tap(() => {
                // Success handled in subscribe
            }))
            .subscribe({
                next: (data) => {
                    FileSaver.saveAs(data, `${this.tender.name} - Suppliers Template.xlsx`);
                    this.toast.success('Suppliers template downloaded successfully');
                },
                error: (error) => {
                    this.toast.error('Failed to download suppliers template');
                    console.error('Download error:', error);
                }
            });
    }

    public downloadSupplierBidTemplate(company: Supplier) {
        if (!company || !company.id) {
            this.toast.error('Invalid supplier company');
            return;
        }

        // Use the first user from the company as the supplier user
        const supplierUser = company.users && company.users.length > 0 ? company.users[0] : null;
        if (!supplierUser) {
            this.toast.error('No users found for this supplier company');
            return;
        }

        this.tenderExportXlsService.fetchTemplate(this.tender, Number(company.id), Number(supplierUser.id))
            .pipe(tap(() => {
                // Success handled in subscribe
            }))
            .subscribe({
                next: (result) => {
                    if (result.type === 'task') {
                        this.toast.success(
                            `Template for ${company.name} will be ready for download in the notification section`,
                            'Requested Template'
                        );
                        return;
                    }
                    const safeName = company.name.replace(/[^a-zA-Z0-9 _\-\.]/g, ' ').replace(/\s+/g, ' ').trim();
                    FileSaver.saveAs(result.blob, `${this.tender.name} - ${safeName} - Bid Template.xlsx`);
                    this.toast.success(`Bid template for ${company.name} downloaded successfully`);
                },
                error: (error) => {
                    this.toast.error(`Failed to download bid template for ${company.name}`);
                    console.error('Download error:', error);
                }
            });
    }

    public getSupplierCount(): number {
        return this.totalItems;
    }

    /**
     * Send reminder email to a specific supplier company
     */
    public confirmSendSupplierReminder(supplier: Supplier) {
        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Send Reminder';
        modalRef.componentInstance.text = `Send reminder email to all users at ${supplier.name}?`;
        modalRef.componentInstance.confirmButtonText = 'Send Reminder';
        modalRef.componentInstance.confirmButtonClass = 'btn-warning';
        modalRef.componentInstance.confirmButtonIcon = 'fas fa-bell';
        modalRef.result.then((confirmed) => {
            if (confirmed) {
                this.service.sendSupplierReminder(this.tender.id, supplier.id).subscribe({
                    next: (response) => {
                        this.toast.success(`Reminder sent to ${response.company_name} (${response.reminder_count} users)`);
                    },
                    error: (error) => {
                        this.toast.error(error.error?.error || 'Failed to send reminder');
                    }
                });
            }
        }, () => {});
    }



    /**
     * Remove a specific user from the tender's invited suppliers
     */
    public confirmRemoveUser(supplier: Supplier, user: Contact) {
        const isLastInvitedUser = (supplier.users || []).length <= 1;
        const fullName = `${user.firstName} ${user.lastName}`.trim();

        const modalRef = this.ngbModal.open(ConfirmModalComponent);
        modalRef.componentInstance.title = 'Revoke Tender Invitation';
        modalRef.componentInstance.lead = 'Attention! This action cannot be undone.';
        modalRef.componentInstance.text = isLastInvitedUser
            ? `${fullName} will lose access to this tender immediately. As the only invited contact, their company will also be removed from the tender. Their user account and your contact record are retained, and any bids already submitted remain in the tender.`
            : `${fullName} will lose access to this tender immediately. Their company remains invited through its other contacts. Their user account and your contact record are retained, and any bids already submitted remain in the tender.`;
        modalRef.componentInstance.details = [
            { label: 'Contact', value: fullName },
            { label: 'Email', value: user.email },
            { label: 'Company', value: supplier.name },
        ];
        modalRef.componentInstance.confirmButtonText = 'Revoke Invitation';
        modalRef.componentInstance.confirmButtonIcon = 'fas fa-user-minus';
        modalRef.result.then((confirmed) => {
            if (confirmed) {
                this.service.removeUser(this.tender.id, user.id).subscribe({
                    next: () => {
                        this.toast.success(`${user.firstName} ${user.lastName}'s invitation to the tender has been revoked`);
                        this.refreshSuppliers(this.selectedTags ? this.selectedTags.map(t => t.id) : [], 1);
                        this.proposalService.onBidSubmitted.emit();
                    },
                    error: (e) => {
                        this.toast.error(e?.error?.error || 'Failed to revoke the tender invitation');
                    }
                });
            }
        }, () => {});
    }

    /**
     * Check if manual bid input should be accessible for sealed tenders
     */
    public canAccessManualBidInput(): boolean {
        if (!this.user.company || !this.tender) {
            return true; // Default to allowing access if no data
        }

        // Role gate: Viewer-role users cannot create or edit mock/manual bids.
        if (!this.user.canManage(this.tender)) {
            return false;
        }

        // For sealed tenders, manual bid input should be restricted
        if (this.sealedTenderService.isSealedTender(this.user.company)) {
            // Manual bid input violates sealed tender principle - always block
            return false;
        }

        return true; // Allow for realtime tenders
    }

    /**
     * Get tooltip message for manual bid input restriction
     */
    public getManualBidInputRestrictionTooltip(): string {
        return 'Manual bid input is not available for sealed tenders to maintain bid confidentiality.';
    }
}
